import { useEffect, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, message, Space, Upload } from 'antd';
import { FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';

import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';

import axios from 'axios';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { BankStatementsService } from 'services/BankStatements';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { BankStatementsTable } from './BankStatementsTable';

const PAGE_TITLE = 'Банковские выписки';

function BankStatements() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [banckStatements, setBanckStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const tribe_id = useTribe();
  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState(null);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    start_date: null,
    begin_date: null,
    f_sum: undefined,
    t_sum: undefined,
    type: null,
  });

  const {
    // state: { drawerVisible, activeRecord },
    openDrawer,
    // closeDrawer,
    // openEditForm,
  } = useDrawers();
  const {
    page,
    totalRows,
    perPage,
    setTotalRows,
    setPagination,
    resetPagination,
  } = usePagination();

  const { client_id: selectedClient } = filters;

  useEffect(() => {
    fetchTurns();
  }, [page, perPage, filters]);

  // const turnsRecordFields = useMemo(() => {
  //   return [
  //     { label: 'Наименование', key: 'doc_id' },
  //     {
  //       label: 'Тип',
  //       key: 'type',
  //       computedValue: turnsTypeMap[activeRecord?.type],
  //     },
  //     {
  //       label: 'Сумма',
  //       key: 'sum',
  //       computedValue: `${priceRender(activeRecord?.sum)} ₸`,
  //     },
  //     {
  //       label: 'Период',
  //       key: 'period',
  //       computedValue: formatDate(activeRecord?.period),
  //     },
  //   ];
  // }, [activeRecord]);

  const fetchTurns = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await BankStatementsService.getBannkStatements(
        page,
        perPage,
        filters,
        tribe_id
      );
      setBanckStatements(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  // const handleDelete = async () => {
  //   const hide = message.loading(REQUEST_LOADING);

  //   try {
  //     const isDeleted = await TurnsService.deleteTurns(activeRecord.id);

  //     if (isDeleted) {
  //       closeDrawer();
  //       fetchTurns();
  //       message.success(SUCCESS_DELETE);
  //     }
  //   } catch (err) {
  //     message.error(DELETE_ERROR);
  //   } finally {
  //     hide();
  //   }
  // };

  const handleUploadStatement = async (data) => {
    const exportFile = data?.file;

    if (!exportFile) {
      message.warning('Загрузите txt файл');
      return;
    }

    const formData = new FormData();
    formData.append('statement_file', exportFile);

    const hide = message.loading(REQUEST_LOADING, 600);

    try {
      const res = await axios.post(
        `org/v1/bank-statements/view/${clientId}/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (res.data) {
        message.success('Успешно');
        setImportData(res.data);
        setImportOpen(true);
      }
    } catch (error) {
      message.error('Не удалось загрузить выписку, повторите попытку');
      setImportOpen(false);
    } finally {
      hide();
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const finalImport = async () => {
    const hide = message.loading(REQUEST_LOADING, 600);

    try {
      const res = await axios.post(
        `org/v1/bank-statements/import/${clientId}/`,
        importData
      );

      if (res.data) {
        message.success('Успешно');
        fetchTurns();
      }
    } catch (error) {
      message.error('Не удалось загрузить выписку, повторите попытку');
    } finally {
      setImportOpen(false);
      hide();
    }
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          clientId={clientId}
          pageTitle={PAGE_TITLE}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="bank-statements" action="create">
          <Space>
            <Upload
              name="file"
              maxCount={1}
              accept=".txt"
              beforeUpload={() => false}
              onChange={handleUploadStatement}
              showUploadList={false}
            >
              <Button
                type="primary"
                disabled={!selectedClient}
                icon={<PlusOutlined />}
              >
                Загрузить
              </Button>
            </Upload>
          </Space>
        </AccessControl>
      </ActionsWrapper>

      <BankStatementsTable
        items={banckStatements}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
        onResetFilter={resetFilters}
      />

      <Drawer
        extra={
          <Space>
            <Button onClick={() => finalImport()} type="primary">
              Загрузить
            </Button>
          </Space>
        }
        open={importOpen}
        onClose={() => setImportOpen(false)}
        width="80%"
      >
        <BankStatementsTable items={importData?.document_section} isImport />
      </Drawer>

      {/* <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.company_name}
        pageId="circulations"
        recordFields={turnsRecordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText={DELETE_CONFIRM}
      /> */}
    </>
  );
}

export { BankStatements };
